/**
 * ISSUE-1344. A pool card's More options, v1's card dropdown: Members, Invite
 * people, Rules and settings, Notifications, and Leave pool, without opening
 * the pool first. The list opens in the shared `Dialog`, a bottom sheet on a
 * phone and a centered dialog on a desktop (`responsive-overlays.mdc`).
 *
 * Every destination is an address the pool screen already owns; this only
 * builds the search for it. Leave pool goes through the shared destructive
 * confirm and is not offered to the Commissioner, who hands the pool over
 * instead.
 *
 * ISSUE-1343. Archive (or Unarchive on the Archived pools screen) tucks the
 * pool away in this fan's own list only, v1's per-user archive. It is
 * undone with one tap from Archived pools, so it needs no confirm. A card
 * that cannot open its pool (a closed pool, a suspended membership) gets a
 * menu with that one row and nothing else.
 */
import { usePoolArchiveMine, usePoolLeave } from "@sportsmet/pool-sdk/private";
import { Button, confirmDestructive, Dialog } from "@sportsmet/ui";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import { ICON_BUTTON_CLASS, MoreIcon } from "../../icons/actionIcons";
import { INVITE_SHEET_NAME } from "../poolInvite/inviteSheetRoute";
import {
  LEAVE_POOL_CONFIRM_BODY,
  LEAVE_POOL_CONFIRM_TITLE,
  LEAVE_POOL_LABEL,
} from "../poolHome/poolHomeState";

export type typePoolCardMenuAction =
  "members" | "invite" | "settings" | "notifications";

/** The `/pools` search each menu row opens. Pure, so it is asserted by name. */
export function poolCardMenuSearch(
  poolId: string,
  action: typePoolCardMenuAction,
): Record<string, string> {
  switch (action) {
    case "members":
      return { poolId, tab: "members" };
    case "invite":
      return { poolId, tab: "members", sheet: INVITE_SHEET_NAME };
    case "settings":
      return { poolId, tab: "settings" };
    case "notifications":
      return { poolId, tab: "settings", panel: "notifications" };
  }
}

const MENU_ROWS: readonly { action: typePoolCardMenuAction; label: string }[] =
  [
    { action: "members", label: "Members" },
    { action: "invite", label: "Invite people" },
    { action: "settings", label: "Rules and settings" },
    { action: "notifications", label: "Notifications" },
  ];

export const ARCHIVE_POOL_LABEL = "Archive";
export const UNARCHIVE_POOL_LABEL = "Unarchive";
export const ARCHIVE_POOL_NOTE =
  "Archive hides this pool from your list. Nobody else sees a change, and you can bring it back from Archived pools.";

export type typePoolCardMenuProps = {
  poolId: string;
  poolName: string;
  /** The Commissioner hands the pool over instead of leaving it. */
  canLeave: boolean;
  /** ISSUE-1343. The row reads Unarchive when this is true. */
  isArchivedByMe?: boolean | undefined;
  /**
   * ISSUE-1343. `archiveOnly` for a card that cannot open its pool: the
   * pool's own destinations and Leave pool would all be refused.
   */
  rows?: "full" | "archiveOnly";
};

export const PoolCardMenu = (props: typePoolCardMenuProps): ReactNode => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const leave = usePoolLeave();
  const archiveMine = usePoolArchiveMine();
  const isArchivedByMe = props.isArchivedByMe === true;
  const isFull = props.rows !== "archiveOnly";

  async function handleArchive(): Promise<void> {
    try {
      const result = await archiveMine.mutateAsync({
        targetPoolId: props.poolId,
        isArchived: !isArchivedByMe,
      });
      if (showApiError(result)) return;
      setIsOpen(false);
    } catch {
      showOfflineError();
    }
  }

  function open(action: typePoolCardMenuAction): void {
    setIsOpen(false);
    void navigate({
      to: "/pools",
      search: poolCardMenuSearch(props.poolId, action),
    });
  }

  async function handleLeave(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: LEAVE_POOL_CONFIRM_TITLE,
      body: LEAVE_POOL_CONFIRM_BODY,
      confirmLabel: LEAVE_POOL_LABEL,
    });
    if (!isConfirmed) return;
    try {
      const result = await leave.mutateAsync({ poolId: props.poolId });
      if (showApiError(result)) return;
      setIsOpen(false);
    } catch {
      showOfflineError();
    }
  }

  return (
    <>
      <button
        type="button"
        className={`${ICON_BUTTON_CLASS} relative z-10 text-ink-secondary`}
        aria-label={`More options for ${props.poolName}`}
        aria-haspopup="dialog"
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
        data-testid={`pool-card-menu-${props.poolId}`}
      >
        <MoreIcon />
      </button>
      <Dialog
        open={isOpen}
        onOpenChange={setIsOpen}
        title={props.poolName}
        description={isFull ? "What do you want to do?" : ARCHIVE_POOL_NOTE}
      >
        <div
          className="flex flex-col gap-2"
          data-testid="pool-card-menu-sheet"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {(isFull ? MENU_ROWS : []).map((row) => (
            <Button
              key={row.action}
              variant="secondary"
              fullWidth
              onClick={() => {
                open(row.action);
              }}
              data-testid={`pool-card-menu-${row.action}`}
            >
              {row.label}
            </Button>
          ))}
          <Button
            variant="secondary"
            fullWidth
            loading={archiveMine.isPending}
            onClick={() => void handleArchive()}
            data-testid="pool-card-menu-archive"
          >
            {isArchivedByMe ? UNARCHIVE_POOL_LABEL : ARCHIVE_POOL_LABEL}
          </Button>
          {isFull && props.canLeave ? (
            <Button
              variant="destructive"
              fullWidth
              loading={leave.isPending}
              onClick={() => void handleLeave()}
              data-testid="pool-card-menu-leave"
            >
              {LEAVE_POOL_LABEL}
            </Button>
          ) : null}
        </div>
      </Dialog>
    </>
  );
};
